import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { openAIService } from '@/lib/ai/openai-service';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';
import { Key, AlertTriangle, Info, Check } from 'lucide-react';

interface AIConfigProps {
  onConfigured: () => void;
}

export function AIConfig({ onConfigured }: AIConfigProps) {
  const [apiKey, setApiKey] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasSavedKey, setHasSavedKey] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  // Check if we already have a saved API key
  useEffect(() => {
    try {
      const savedKey = localStorage.getItem('developer-openai-api-key');
      if (savedKey) {
        setApiKey(savedKey);
        setHasSavedKey(true);
      }
    } catch (error) {
      console.warn('Error accessing localStorage:', error);
    }
  }, []);

  const handleSave = () => {
    if (!apiKey.trim()) {
      toast({
        variant: 'destructive',
        title: 'API Key Required',
        description: 'Please enter your OpenAI API key to continue.',
      });
      return;
    }

    setIsLoading(true);

    // Simulate API validation
    setTimeout(() => {
      try {
        // Configure the OpenAI service with the API key
        openAIService.configure(apiKey);
        
        toast({
          title: 'API Key Saved',
          message: 'Your OpenAI API key has been saved successfully.',
        });
        
        onConfigured();
      } catch (error) {
        console.error('Error saving API key:', error);
        toast({
          variant: 'destructive',
          title: 'Error',
          message: 'Failed to save API key. Please try again.',
        });
      } finally {
        setIsLoading(false);
      }
    }, 1000);
  };

  // Only allow admins to access this page
  if (user?.role !== 'admin') {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-4rem)] p-6">
        <Card className="max-w-md w-full p-6">
          <div className="flex flex-col items-center text-center space-y-4">
            <div className="rounded-full bg-destructive/10 p-3">
              <AlertTriangle className="h-6 w-6 text-destructive" />
            </div>
            <h2 className="text-xl font-semibold">Access Denied</h2>
            <p className="text-muted-foreground">
              Only administrators can configure the AI settings. Please contact your administrator for assistance.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center h-[calc(100vh-4rem)] p-6">
      <Card className="max-w-md w-full">
        <CardHeader>
          <div className="flex items-center gap-2">
            <div className="rounded-full bg-primary/10 p-2">
              <Key className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle>Developer API Configuration</CardTitle>
              <CardDescription>
                Configure the OpenAI API key for FlexHelp AI
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="api-key">OpenAI API Key</Label>
            <Input
              id="api-key"
              type="password"
              placeholder="sk-..."
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              This key will be used for all users of the application. It is stored securely in your browser's local storage.
            </p>
          </div>

          {hasSavedKey && (
            <div className="flex items-start gap-2 p-3 bg-primary/5 rounded-md">
              <Check className="h-5 w-5 text-green-500 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium">API Key Already Configured</p>
                <p className="text-muted-foreground">
                  You can update the key if needed, or keep the existing one.
                </p>
              </div>
            </div>
          )}

          <div className="flex items-start gap-2 p-3 bg-amber-50 dark:bg-amber-950/20 rounded-md">
            <Info className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium">Developer Setting</p>
              <p className="text-muted-foreground">
                This is a developer-only setting. Regular users will not need to configure an API key.
              </p>
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button variant="outline" onClick={onConfigured}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save API Key'}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
} 